import React, { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { eventsApi } from '../utils/api';
import { useTheme } from '../context/ThemeContext';

const PODIUM_BG = {
  1: 'rgba(232, 168, 56, 0.09)',
  2: 'rgba(180, 180, 190, 0.06)',
  3: 'rgba(185, 130, 80, 0.06)'
};

const TYPE_LABELS = {
  box_weight: 'Box Weight',
  heaviest_single: 'Heaviest Single Fish',
  cumulative_weight: 'Cumulative Weight',
  most_fish: 'Most Fish',
  calcutta: 'Calcutta'
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTimestamp(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' at ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function formatWeight(w, decimals = 2) {
  if (!w || w === 0) return '\u2014';
  return w.toFixed(decimals) + ' lbs';
}

function formatPoints(p) {
  if (!p || p === 0) return '\u2014';
  return p.toFixed(2) + ' pts';
}

function escapeCsvField(val) {
  const str = String(val ?? '');
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
}

function downloadCSV(filename, headers, rows) {
  const bom = '\uFEFF';
  const csv = bom + [headers.map(escapeCsvField).join(','),
    ...rows.map(row => row.map(escapeCsvField).join(','))
  ].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function handleExportCSV(tourn, eventName) {
  const safeName = (eventName || 'Event').replace(/\s+/g, '_');
  const tournName = (tourn.name || 'Tournament').replace(/\s+/g, '_');
  const filename = `${safeName}_${tournName}.csv`;

  const wd = tourn.weight_decimals ?? 2;
  if (tourn.type === 'daily_winners' || tourn.type === 'calcutta_daily') {
    const headers = ['Day', 'Winner', 'Weight (lbs)', 'Payout $'];
    const rows = (tourn.daily_results || []).map(d => [
      `Day ${d.day_number}`,
      d.winner ? `${d.winner.team_number ? `#${d.winner.team_number} ` : ''}${d.winner.team_name}` : '',
      d.winner ? d.winner.weight_lbs.toFixed(wd) : '0',
      ''
    ]);
    downloadCSV(filename, headers, rows);
  } else if (tourn.type === 'calcutta') {
    const headers = ['Rank', 'Team #', 'Team Name', tourn.calcutta_track_label || 'Value', 'Payout $'];
    const rows = (tourn.rankings || []).map(r => [
      r.rank,
      r.team_number ? `#${r.team_number}` : '',
      r.team_name,
      r.value > 0 ? r.value.toFixed(2) : '0',
      ''
    ]);
    downloadCSV(filename, headers, rows);
  } else {
    const cols = tourn.scoreboard_columns || {};
    const showTeamNum = cols.team_number !== false;
    const showFishCount = cols.fish_count !== false;
    const showSpecies = cols.species != null ? cols.species : !!tourn.track_species;
    const showWeight = cols.weight !== false;
    const showBigFish = (cols.big_fish != null ? cols.big_fish : true) && tourn.has_big_fish_contest;
    const showPoints = cols.points !== false && tourn.scoring_mode !== 'none';
    const headers = ['Rank',
      ...(showTeamNum ? ['Team #'] : []),
      'Team Name',
      ...(showFishCount ? ['Fish'] : []),
      ...(showSpecies ? ['Species'] : []),
      ...(showWeight ? ['Weight (lbs)'] : []),
      ...(showBigFish ? ['Big Fish (lbs)'] : []),
      ...(showPoints ? ['Score (pts)'] : []),
      'Payout $'
    ];
    const rows = (tourn.standings || []).map(t => [
      t.rank,
      ...(showTeamNum ? [t.team_number ? `#${t.team_number}` : ''] : []),
      t.team_name,
      ...(showFishCount ? [t.total_fish] : []),
      ...(showSpecies ? [
        t.species_catches && t.species_catches.length > 0
          ? t.species_catches.map(sc => `${sc.species} (${sc.count})`).join(', ')
          : ''
      ] : []),
      ...(showWeight ? [t.total_weight > 0 ? t.total_weight.toFixed(tourn.weight_decimals ?? 2) : '0'] : []),
      ...(showBigFish ? [t.heaviest_fish > 0 ? t.heaviest_fish.toFixed(tourn.weight_decimals ?? 2) : '0'] : []),
      ...(showPoints ? [t.total_points > 0 ? t.total_points.toFixed(2) : '0'] : []),
      ''
    ]);
    downloadCSV(filename, headers, rows);
  }
}

function rankDisplay(rank, forPdf) {
  if (rank <= 3) {
    const medals = ['\u{1F947}', '\u{1F948}', '\u{1F949}'];
    const labels = ['1st', '2nd', '3rd'];
    if (forPdf) return labels[rank - 1];
    return (
      <span>
        <span className="report-rank-emoji">{medals[rank - 1]}</span>
        <span className="report-rank-text" style={{ display: 'none' }}>{labels[rank - 1]}</span>
      </span>
    );
  }
  return rank;
}

export default function ReportsTab({ eventId, event: parentEvent }) {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [error, setError] = useState(null);
  const printRef = useRef(null);
  const { siteName, logoUrl, logoDarkUrl } = useTheme();

  const headerBg = parentEvent?.scoreboard_table_header_bg || '#e8a838';
  const headerColor = parentEvent?.scoreboard_table_header_color || '#0d1b2a';
  const scoreboardUrl = `${window.location.origin}/events/${eventId}/scoreboard`;

  useEffect(() => { fetchReport(); }, [eventId]);

  async function fetchReport() {
    setLoading(true);
    setError(null);
    try {
      const res = await eventsApi.getEventReport(eventId);
      setReportData(res.data.data);
    } catch (err) {
      console.error('Report fetch error:', err);
      setError(err.response?.data?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }

  async function handleDownloadPDF() {
    setPdfGenerating(true);
    try {
      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
        import('jspdf'),
        import('html2canvas')
      ]);

      const element = printRef.current;
      // Show plain text ranks for PDF
      element.classList.add('pdf-mode');

      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        backgroundColor: '#ffffff'
      });

      element.classList.remove('pdf-mode');

      const imgData = canvas.toDataURL('image/jpeg', 0.8);
      const pdf = new jsPDF('p', 'mm', 'letter');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      const pageHeight = pdf.internal.pageSize.getHeight();

      let heightLeft = pdfHeight;
      let position = 0;

      pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, pdfHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, pdfHeight);
        heightLeft -= pageHeight;
      }

      const eventName = reportData?.event?.name || 'Event';
      const filename = `${eventName.replace(/\s+/g, '_')}_Reports.pdf`;
      pdf.save(filename);
    } catch (err) {
      console.error('PDF generation error:', err);
      alert('Failed to generate PDF');
    } finally {
      setPdfGenerating(false);
    }
  }

  if (loading) return <div className="spinner" />;
  if (error) return <div style={{ padding: 40, textAlign: 'center', color: '#ff6b6b' }}>{error}</div>;
  if (!reportData || !reportData.tournaments?.length) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#8a9bb5' }}>No tournaments found for this event.</div>;
  }

  const { event: evt, generated_at, tournaments } = reportData;

  return (
    <div>
      {/* Control Bar — hidden on print */}
      <div className="report-controls-bar" style={{
        display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24,
        padding: '12px 16px', background: 'rgba(255,255,255,0.04)', borderRadius: 8,
        flexWrap: 'wrap'
      }}>
        <button onClick={fetchReport} className="btn btn-secondary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          &#x21bb; Refresh
        </button>
        <span style={{ color: '#8a9bb5', fontSize: '0.85rem' }}>
          Generated: {formatTimestamp(generated_at)}
        </span>
        <div style={{ flex: 1 }} />
        <button onClick={() => window.print()} className="btn btn-secondary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          &#x1F5A8; Print
        </button>
        <button onClick={handleDownloadPDF} disabled={pdfGenerating} className="btn btn-primary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {pdfGenerating ? 'Generating PDF...' : '\u2B07 Download PDF'}
        </button>
      </div>

      {/* Report Print Area */}
      <div id="reports-print-area" ref={printRef} style={{ background: '#ffffff', padding: 24, borderRadius: 8 }}>

        {/* ── QR Code Scoreboard Page ─────────────────────────── */}
        <div className="tournament-report-block qr-page" style={{
          background: '#ffffff',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '48px 40px',
          boxSizing: 'border-box',
          pageBreakAfter: 'always',
          breakAfter: 'page'
        }}>
          {/* Logo — prefer dark/print variant, fall back to regular logo, then text */}
          <div style={{ textAlign: 'center', width: '100%' }}>
            {(logoDarkUrl || logoUrl) ? (
              <img src={logoDarkUrl || logoUrl} alt={siteName} style={{ maxHeight: 100, maxWidth: 360, objectFit: 'contain' }} />
            ) : (
              <div style={{
                fontFamily: 'Oswald, Arial, sans-serif',
                fontSize: 48,
                fontWeight: 700,
                color: '#0d1b2a',
                letterSpacing: '0.1em',
                textTransform: 'uppercase'
              }}>
                {siteName}
              </div>
            )}
          </div>

          {/* QR Code + label */}
          <div style={{ textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 24 }}>
            <div style={{
              fontFamily: 'Oswald, Arial, sans-serif',
              fontSize: 22,
              fontWeight: 700,
              color: '#0d1b2a',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              marginBottom: 8
            }}>
              Live Scoreboard
            </div>
            <div style={{
              padding: 20,
              border: '3px solid #0d1b2a',
              borderRadius: 12,
              background: '#ffffff',
              display: 'inline-block'
            }}>
              <QRCodeSVG
                value={scoreboardUrl}
                size={280}
                bgColor="#ffffff"
                fgColor="#0d1b2a"
                level="H"
              />
            </div>
            <div style={{
              fontFamily: 'Oswald, Arial, sans-serif',
              fontSize: 16,
              color: '#555',
              marginTop: 8,
              wordBreak: 'break-all',
              maxWidth: 400,
              textAlign: 'center'
            }}>
              {scoreboardUrl}
            </div>
            {evt.name && (
              <div style={{
                fontFamily: 'Oswald, Arial, sans-serif',
                fontSize: 18,
                color: '#0d1b2a',
                fontWeight: 600,
                textAlign: 'center'
              }}>
                {evt.name}
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{
            textAlign: 'center',
            fontFamily: 'Oswald, Arial, sans-serif',
            fontSize: 22,
            fontWeight: 700,
            color: '#0d1b2a',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            paddingTop: 16,
            borderTop: '2px solid #0d1b2a',
            width: '100%'
          }}>
            derbyscores.com
          </div>
        </div>

        {tournaments.map((tourn, idx) => {
          const wd = tourn.weight_decimals ?? 2;
          const fmtWt = (w) => formatWeight(w, wd);
          return (
          <div key={tourn.id} className="tournament-report-block" style={idx > 0 ? { borderTop: '3px solid #0d1b2a', marginTop: 40, paddingTop: 32 } : {}}>
            {/* Report Header */}
            <div className="report-header" style={{
              border: '2px solid #0d1b2a', padding: '14px 18px', marginBottom: 20,
              fontFamily: 'Oswald, Arial, sans-serif', color: '#0d1b2a'
            }}>
              <div style={{ fontSize: 20, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                {evt.name}
              </div>
              <div style={{ fontSize: 14, marginTop: 4, color: '#333' }}>
                {tourn.name.replace(/\s*\(Day\s*\d+\)\s*$/, '')} &middot; {tourn.tournament_type_label || TYPE_LABELS[tourn.tournament_type] || tourn.tournament_type}
                {tourn.type === 'calcutta' && tourn.calcutta_track_label && (
                  <span style={{ fontSize: 12, color: '#777' }}> &mdash; Tracking: {tourn.calcutta_track_label}</span>
                )}
              </div>
              <div style={{ fontSize: 13, marginTop: 2, color: '#555' }}>
                {formatDate(evt.start_date)}{evt.end_date !== evt.start_date ? ` \u2013 ${formatDate(evt.end_date)}` : ''} &middot; {evt.location || 'No location'}
              </div>
              <div style={{ fontSize: 12, marginTop: 2, color: '#777' }}>
                Organizer: {evt.organizer_name} &nbsp;&middot;&nbsp; Generated: {formatTimestamp(generated_at)}
              </div>
            </div>

            {tourn.type === 'calcutta_daily' ? (
              /* ── Calcutta Daily Heaviest Fish Table ── */
              <>
                {(!tourn.daily_results || tourn.daily_results.length === 0) ? (
                  <div style={{ padding: '40px 0', textAlign: 'center', color: '#8a9bb5', fontSize: 13 }}>
                    Waiting for results...
                  </div>
                ) : (
                  <table style={{
                    width: '100%', maxWidth: 600, borderCollapse: 'collapse',
                    fontFamily: 'Oswald, Arial, sans-serif', fontSize: 13, color: '#0d1b2a'
                  }}>
                    <thead>
                      <tr>
                        {['Day', 'Team', 'Weight', 'Payout $'].map((col, ci) => (
                          <th key={ci} className={col === 'Payout $' ? 'col-payout' : ''} style={{
                            padding: '8px 10px',
                            background: col === 'Payout $' ? '#f5f5f5' : headerBg,
                            color: col === 'Payout $' ? '#666' : headerColor,
                            borderBottom: '2px solid #0d1b2a',
                            textAlign: col === 'Team' ? 'left' : col === 'Payout $' || col === 'Weight' ? 'right' : 'center',
                            fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5,
                            ...(col === 'Payout $' ? { border: '1.5px dashed #bbb', minWidth: 90 } : {})
                          }}>
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tourn.daily_results.map((day, di) => (
                        <tr key={di}>
                          <td style={{ padding: '7px 10px', textAlign: 'center', borderBottom: '1px solid #eee', fontWeight: 700 }}>Day {day.day_number}</td>
                          <td style={{ padding: '7px 10px', textAlign: 'left', borderBottom: '1px solid #eee', fontWeight: 600 }}>
                            {day.winner ? (
                              <>
                                {day.winner.team_number ? <span style={{ color: '#e8a838', marginRight: 8, fontSize: 13, fontWeight: 600 }}>#{day.winner.team_number}</span> : null}
                                {day.winner.team_name}
                                {day.winner.species ? <span style={{ color: '#888', fontSize: 11, marginLeft: 6 }}>({day.winner.species})</span> : null}
                              </>
                            ) : <span style={{ color: '#ccc', fontStyle: 'italic' }}>Pending</span>}
                          </td>
                          <td style={{ padding: '7px 10px', textAlign: 'right', borderBottom: '1px solid #eee', color: '#e8a838', fontWeight: 700 }}>
                            {day.winner ? `${day.winner.weight_lbs.toFixed(wd)} lbs` : '\u2014'}
                          </td>
                          <td className="col-payout-data" style={{
                            padding: '7px 10px', textAlign: 'right',
                            borderBottom: '1.5px dashed #bbb', minWidth: 90
                          }}></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            ) : tourn.type === 'daily_winners' ? (
              /* ── Custom Daily Winners Table ── */
              <>
                {(!tourn.daily_results || tourn.daily_results.length === 0) ? (
                  <div style={{ padding: '40px 0', textAlign: 'center', color: '#8a9bb5', fontSize: 13 }}>
                    No days configured yet.
                  </div>
                ) : (
                  <table style={{
                    width: '100%', maxWidth: 600, borderCollapse: 'collapse',
                    fontFamily: 'Oswald, Arial, sans-serif', fontSize: 13, color: '#0d1b2a'
                  }}>
                    <thead>
                      <tr>
                        {['Day', 'Winner', 'Weight', 'Payout $'].map((col, ci) => (
                          <th key={ci} className={col === 'Payout $' ? 'col-payout' : ''} style={{
                            padding: '8px 10px',
                            background: col === 'Payout $' ? '#f5f5f5' : headerBg,
                            color: col === 'Payout $' ? '#666' : headerColor,
                            borderBottom: '2px solid #0d1b2a',
                            textAlign: col === 'Winner' ? 'left' : col === 'Payout $' || col === 'Weight' ? 'right' : 'center',
                            fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5,
                            ...(col === 'Payout $' ? { border: '1.5px dashed #bbb', minWidth: 90 } : {})
                          }}>
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tourn.daily_results.map((day, di) => (
                        <tr key={di}>
                          <td style={{ padding: '7px 10px', textAlign: 'center', borderBottom: '1px solid #eee', fontWeight: 700 }}>
                            Day {day.day_number}
                            {day.is_rolled_over && <span style={{ color: '#ff9f0a', fontSize: 10, marginLeft: 6 }}>ROLLOVER</span>}
                          </td>
                          <td style={{ padding: '7px 10px', textAlign: 'left', borderBottom: '1px solid #eee', fontWeight: 600 }}>
                            {day.winner ? (
                              <>
                                {day.winner.team_number ? <span style={{ color: '#e8a838', marginRight: 8, fontSize: 13, fontWeight: 600 }}>#{day.winner.team_number}</span> : null}
                                {day.winner.team_name}
                              </>
                            ) : <span style={{ color: '#ccc', fontStyle: 'italic' }}>{day.is_rolled_over ? 'No qualifier — rolled over' : 'Pending'}</span>}
                          </td>
                          <td style={{ padding: '7px 10px', textAlign: 'right', borderBottom: '1px solid #eee', color: '#e8a838', fontWeight: 700 }}>
                            {day.winner ? `${day.winner.weight_lbs.toFixed(wd)} lbs` : '\u2014'}
                          </td>
                          <td className="col-payout-data" style={{
                            padding: '7px 10px', textAlign: 'right',
                            borderBottom: '1.5px dashed #bbb', minWidth: 90
                          }}></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            ) : tourn.type === 'calcutta' ? (
              /* ── Calcutta Table ── */
              <>
                {(!tourn.rankings || tourn.rankings.length === 0) ? (
                  <div style={{ padding: '40px 0', textAlign: 'center', color: '#8a9bb5', fontSize: 13 }}>
                    Waiting for main event results...
                  </div>
                ) : (
                  <table style={{
                    width: '100%', maxWidth: 600, borderCollapse: 'collapse',
                    fontFamily: 'Oswald, Arial, sans-serif', fontSize: 13, color: '#0d1b2a'
                  }}>
                    <thead>
                      <tr>
                        {['Pos', 'Team', tourn.calcutta_track_label || 'Value', 'Payout $'].map((col, ci) => (
                          <th key={ci} className={col === 'Payout $' ? 'col-payout' : ''} style={{
                            padding: '8px 10px',
                            background: col === 'Payout $' ? '#f5f5f5' : headerBg,
                            color: col === 'Payout $' ? '#666' : headerColor,
                            borderBottom: '2px solid #0d1b2a',
                            textAlign: col === 'Team' ? 'left' : col === 'Payout $' ? 'right' : 'center',
                            fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5,
                            ...(col === 'Payout $' ? { border: '1.5px dashed #bbb', minWidth: 90 } : {})
                          }}>
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tourn.rankings.map((r, ri) => {
                        const isZero = r.value === 0;
                        const rowBg = !isZero && PODIUM_BG[r.rank] ? PODIUM_BG[r.rank] : 'transparent';
                        return (
                          <tr key={r.team_id} className={r.rank <= 3 && !isZero ? `rank-${r.rank}` : ''} style={{ background: rowBg }}>
                            <td style={{ padding: '7px 10px', textAlign: 'center', borderBottom: '1px solid #eee', fontSize: r.rank <= 3 && !isZero ? 18 : 14 }}>
                              {isZero ? r.rank : rankDisplay(r.rank, false)}
                            </td>
                            <td style={{ padding: '7px 10px', textAlign: 'left', borderBottom: '1px solid #eee', fontWeight: 600 }}>
                              {r.team_number ? <span style={{ color: '#e8a838', marginRight: 8, fontSize: 13, fontWeight: 600 }}>#{r.team_number}</span> : null}
                              {r.team_name}
                            </td>
                            <td style={{ padding: '7px 10px', textAlign: 'center', borderBottom: '1px solid #eee', color: '#e8a838', fontWeight: 700 }}>
                              {r.value > 0 ? r.value.toFixed(2) : '\u2014'}
                            </td>
                            <td className="col-payout-data" style={{
                              padding: '7px 10px', textAlign: 'right',
                              borderBottom: '1.5px dashed #bbb', minWidth: 90
                            }}></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </>
            ) : (
              /* ── Regular Standings Table ── */
              (() => {
                const cols = tourn.scoreboard_columns || {};
                const rShowTeamNum = cols.team_number !== false;
                const rShowFishCount = cols.fish_count !== false;
                const rShowSpecies = cols.species != null ? cols.species : !!tourn.track_species;
                const rShowWeight = cols.weight !== false;
                const rShowBigFish = (cols.big_fish != null ? cols.big_fish : true) && tourn.has_big_fish_contest;
                const rShowPoints = cols.points !== false && tourn.scoring_mode !== 'none';
                const reportCols = ['Rank',
                  ...(rShowTeamNum ? ['Team #'] : []),
                  'Team Name',
                  ...(rShowFishCount ? ['Fish'] : []),
                  ...(rShowSpecies ? ['Species'] : []),
                  ...(rShowWeight ? ['Weight'] : []),
                  ...(rShowBigFish ? ['Big Fish'] : []),
                  ...(rShowPoints ? ['Score'] : []),
                  'Payout $'
                ];
                return (
              <>
                <table style={{
                  width: '100%', borderCollapse: 'collapse', fontFamily: 'Oswald, Arial, sans-serif',
                  fontSize: 13, color: '#0d1b2a'
                }}>
                  <thead>
                    <tr>
                      {reportCols.map((col, ci) => (
                        <th key={ci} className={col === 'Payout $' ? 'col-payout' : ''} style={{
                          padding: '8px 10px',
                          background: col === 'Payout $' ? '#f5f5f5' : headerBg,
                          color: col === 'Payout $' ? '#666' : headerColor,
                          borderBottom: '2px solid #0d1b2a',
                          textAlign: ['Team Name', 'Species'].includes(col) ? 'left' :
                                     ['Weight', 'Big Fish', 'Score', 'Payout $'].includes(col) ? 'right' : 'center',
                          fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5,
                          ...(col === 'Payout $' ? { border: '1.5px dashed #bbb', minWidth: 90 } : {})
                        }}>
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tourn.standings.map((team, ti) => {
                      const isZero = team.total_fish === 0 && team.total_points === 0 && team.total_weight === 0;
                      const rowBg = !isZero && PODIUM_BG[team.rank] ? PODIUM_BG[team.rank] : 'transparent';
                      return (
                        <tr key={team.team_id} className={team.rank <= 3 && !isZero ? `rank-${team.rank}` : ''} style={{ background: rowBg }}>
                          <td style={{ padding: '7px 10px', textAlign: 'center', borderBottom: '1px solid #eee', fontSize: team.rank <= 3 && !isZero ? 18 : 14 }}>
                            {isZero ? team.rank : rankDisplay(team.rank, false)}
                          </td>
                          {rShowTeamNum && (
                            <td style={{ padding: '7px 10px', textAlign: 'center', borderBottom: '1px solid #eee', color: '#e8a838', fontWeight: 600, fontSize: 13 }}>
                              {team.team_number ? `#${team.team_number}` : '\u2014'}
                            </td>
                          )}
                          <td style={{ padding: '7px 10px', textAlign: 'left', borderBottom: '1px solid #eee', fontWeight: 600 }}>
                            {team.team_name}
                          </td>
                          {rShowFishCount && (
                            <td style={{ padding: '7px 10px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                              {team.total_fish > 0 ? team.total_fish : '\u2014'}
                            </td>
                          )}
                          {rShowSpecies && (
                            <td style={{ padding: '7px 10px', textAlign: 'left', borderBottom: '1px solid #eee', fontSize: 12 }}>
                              {team.species_catches && team.species_catches.length > 0
                                ? team.species_catches.map(sc => `${sc.species} (${sc.count})`).join(', ')
                                : '\u2014'}
                            </td>
                          )}
                          {rShowWeight && (
                            <td style={{ padding: '7px 10px', textAlign: 'right', borderBottom: '1px solid #eee' }}>
                              {fmtWt(team.total_weight)}
                            </td>
                          )}
                          {rShowBigFish && (
                            <td style={{ padding: '7px 10px', textAlign: 'right', borderBottom: '1px solid #eee', color: '#2196f3' }}>
                              {fmtWt(team.heaviest_fish)}
                            </td>
                          )}
                          {rShowPoints && (
                            <td style={{ padding: '7px 10px', textAlign: 'right', borderBottom: '1px solid #eee', color: '#e8a838', fontWeight: 700 }}>
                              {formatPoints(team.total_points)}
                            </td>
                          )}
                          <td className="col-payout-data" style={{
                            padding: '7px 10px', textAlign: 'right',
                            borderBottom: '1.5px dashed #bbb', minWidth: 90
                          }}></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Zero-catch note */}
                {tourn.standings.every(s => s.total_fish === 0) && (
                  <div style={{ padding: '10px 0', color: '#8a9bb5', fontSize: 12, fontStyle: 'italic' }}>
                    No catches recorded yet — standings will update as entries are logged.
                  </div>
                )}

                {/* Big Fish Callout */}
                {tourn.big_fish && (
                  <div className="big-fish-callout" style={{
                    border: '1.5px solid #0d1b2a', padding: '8px 14px', marginTop: 12,
                    fontFamily: 'Oswald, Arial, sans-serif', fontWeight: 700, fontSize: 14, color: '#0d1b2a',
                    display: 'flex', alignItems: 'center', gap: 8
                  }}>
                    <span>&#x1F3C6;</span>
                    <span>BIG FISH LEADER: {tourn.big_fish.team_name} &mdash; {tourn.big_fish.weight_lbs.toFixed(wd)} lbs{tourn.big_fish.species ? ` (${tourn.big_fish.species})` : ''}</span>
                  </div>
                )}

                {/* Species Breakdown */}
                {tourn.track_species && tourn.species_breakdown && tourn.species_breakdown.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{
                      fontFamily: 'Oswald, Arial, sans-serif', fontWeight: 700, fontSize: 13,
                      textTransform: 'uppercase', letterSpacing: 0.5, color: '#0d1b2a', marginBottom: 6
                    }}>
                      Catches by Species
                    </div>
                    <table style={{
                      width: '100%', maxWidth: 500, borderCollapse: 'collapse',
                      fontFamily: 'Oswald, Arial, sans-serif', fontSize: 13, color: '#0d1b2a'
                    }}>
                      <thead>
                        <tr>
                          {['Species', 'Catches', 'Total Weight', 'Heaviest'].map((col, ci) => (
                            <th key={ci} style={{
                              padding: '6px 10px',
                              background: headerBg, color: headerColor,
                              borderBottom: '2px solid #0d1b2a',
                              textAlign: ci === 0 ? 'left' : 'right',
                              fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5
                            }}>
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {tourn.species_breakdown.map((s, si) => (
                          <tr key={si}>
                            <td style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #eee', fontWeight: 600 }}>{s.species}</td>
                            <td style={{ padding: '6px 10px', textAlign: 'right', borderBottom: '1px solid #eee' }}>{s.catch_count}</td>
                            <td style={{ padding: '6px 10px', textAlign: 'right', borderBottom: '1px solid #eee' }}>{fmtWt(s.total_weight)}</td>
                            <td style={{ padding: '6px 10px', textAlign: 'right', borderBottom: '1px solid #eee', color: '#2196f3' }}>{fmtWt(s.heaviest)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
                );
              })()
            )}

            {/* Summary Footer */}
            <div className="report-summary" style={{
              marginTop: 14, fontSize: 12, color: '#555',
              fontFamily: 'Oswald, Arial, sans-serif'
            }}>
              <div>
                Teams Entered: {tourn.summary.total_teams} &nbsp;&middot;&nbsp;
                Teams with Catches: {tourn.summary.teams_with_entries} &nbsp;&middot;&nbsp;
                Teams with No Entries: {tourn.summary.teams_no_entries}
              </div>
              {tourn.summary.teams_no_entries > 0 && (
                <div style={{ marginTop: 4, color: '#888' }}>
                  No entries recorded: {tourn.summary.no_entry_team_names.join(', ')}
                </div>
              )}
            </div>

            {/* CSV Export — hidden on print */}
            <div className="report-controls-bar" style={{ marginTop: 12 }}>
              <button
                onClick={() => handleExportCSV(tourn, evt.name)}
                className="btn btn-secondary btn-sm"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8rem' }}
              >
                &#x2B07; Export CSV
              </button>
            </div>
          </div>
        );})}
      </div>

      {/* Print & PDF CSS */}
      <style>{`
        /* Override dark-theme global td/th/thead styles inside report area */
        #reports-print-area td { color: #0d1b2a; border-bottom: 1px solid #eee; }
        #reports-print-area th { color: inherit; }
        #reports-print-area thead { background: transparent; }
        @media print {
          body * { visibility: hidden !important; }
          #reports-print-area,
          #reports-print-area * { visibility: visible !important; }
          #reports-print-area {
            position: absolute !important;
            left: 0; top: 0; width: 100%;
            margin: 0; padding: 12px !important;
            background: #fff !important;
          }
          .report-controls-bar { display: none !important; }
          .tournament-report-block { page-break-after: always; break-after: page; }
          .tournament-report-block:last-child { page-break-after: avoid; break-after: avoid; }
          .qr-page { min-height: 100vh !important; page-break-after: always !important; break-after: page !important; background: #fff !important; }
          #reports-print-area table { width: 100%; border-collapse: collapse; font-size: 10pt; font-family: Arial, sans-serif; }
          #reports-print-area th,
          #reports-print-area td { border: 1px solid #000; padding: 5px 8px; text-align: left; color: #000 !important; background: #fff !important; }
          #reports-print-area thead th { background: #f0f0f0 !important; font-weight: bold; }
          #reports-print-area .col-payout { border: 1.5px dashed #444 !important; min-width: 90px; }
          #reports-print-area .col-payout-data { border-left: 1px solid #000 !important; border-right: 1px solid #000 !important; border-top: 1px solid #000 !important; border-bottom: 1.5px dashed #444 !important; }
          .report-header { border: 2px solid #000; padding: 10px 14px; margin-bottom: 14px; font-family: Arial, sans-serif; }
          .big-fish-callout { border: 1.5px solid #000; padding: 6px 10px; margin-top: 8px; font-weight: bold; font-family: Arial, sans-serif; }
          .report-summary { margin-top: 10px; font-size: 9pt; font-family: Arial, sans-serif; }
          .rank-1 td, .rank-2 td, .rank-3 td { font-weight: bold; }
          .report-rank-emoji { display: none !important; }
          .report-rank-text { display: inline !important; }
        }
        .pdf-mode .report-rank-emoji { display: none !important; }
        .pdf-mode .report-rank-text { display: inline !important; }
      `}</style>
    </div>
  );
}
